import React, { useState, useEffect, useCallback } from 'react';
import axios from 'axios';
import { Layout } from '@/components/Layout';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from '@/components/ui/dialog';
import { AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent, AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle } from '@/components/ui/alert-dialog';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { toast } from 'sonner';
import { Plus, RefreshCw, Calendar, Archive, Edit, Play } from 'lucide-react';

const API_URL = process.env.REACT_APP_BACKEND_URL || '';

const emptyFormData = {
  client_id: '',
  location_id: '',
  assigned_cleaner_id: '',
  start_date: new Date().toISOString().split('T')[0],
  preferred_time: '09:00',
  estimated_duration_minutes: 60,
  notes: '',
  frequency: 'WEEKLY',
  custom_interval_days: 7
};

const frequencyLabels = {
  WEEKLY: 'Weekly (every 7 days)',
  BIWEEKLY: 'Biweekly (every 14 days)',
  FOURWEEKLY: 'Four-weekly (every 28 days)',
  CUSTOM: 'Custom interval'
};

export default function RecurringJobs() {
  const [recurringJobs, setRecurringJobs] = useState([]);
  const [clients, setClients] = useState([]);
  const [locations, setLocations] = useState([]);
  const [cleaners, setCleaners] = useState([]);
  const [loading, setLoading] = useState(true);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [editDialogOpen, setEditDialogOpen] = useState(false);
  const [archiveDialogOpen, setArchiveDialogOpen] = useState(false);
  const [generateDialogOpen, setGenerateDialogOpen] = useState(false);
  const [selectedJob, setSelectedJob] = useState(null);
  const [jobToArchive, setJobToArchive] = useState(null);
  const [jobToGenerate, setJobToGenerate] = useState(null);
  const [filter, setFilter] = useState('active');
  const [weeksToGenerate, setWeeksToGenerate] = useState(4);
  const [generating, setGenerating] = useState(false);
  
  const [createFormData, setCreateFormData] = useState(emptyFormData);
  const [editFormData, setEditFormData] = useState(emptyFormData);
  const [filteredLocations, setFilteredLocations] = useState([]);

  const fetchRecurringJobs = useCallback(async (currentFilter) => {
    try {
      let url = `${API_URL}/api/recurring-jobs`;
      if (currentFilter === 'active') {
        url += '?is_active=true';
      } else if (currentFilter === 'archived') {
        url += '?is_active=false';
      }
      const response = await axios.get(url);
      setRecurringJobs(response.data);
    } catch (error) {
      toast.error('Failed to fetch recurring jobs');
    } finally {
      setLoading(false);
    }
  }, []);

  const fetchClients = useCallback(async () => {
    try {
      const response = await axios.get(`${API_URL}/api/clients?is_active=true`);
      setClients(response.data);
    } catch (error) {
      toast.error('Failed to fetch clients');
    }
  }, []);

  const fetchLocations = useCallback(async () => {
    try {
      const response = await axios.get(`${API_URL}/api/locations?is_active=true`);
      setLocations(response.data);
    } catch (error) {
      toast.error('Failed to fetch locations');
    }
  }, []);

  const fetchCleaners = useCallback(async () => {
    try {
      const response = await axios.get(`${API_URL}/api/users`);
      setCleaners(response.data.filter(u => u.role === 'CLEANER' && u.is_active));
    } catch (error) {
      toast.error('Failed to fetch cleaners');
    }
  }, []);

  useEffect(() => {
    fetchRecurringJobs(filter);
    fetchClients();
    fetchLocations();
    fetchCleaners();
  }, [filter, fetchRecurringJobs, fetchClients, fetchLocations, fetchCleaners]);

  // Filter locations when client changes (create form)
  useEffect(() => {
    if (createFormData.client_id) {
      setFilteredLocations(locations.filter(l => l.client_id === createFormData.client_id));
    } else {
      setFilteredLocations([]);
    }
  }, [createFormData.client_id, locations]);

  const handleSubmit = async (e) => {
    e.preventDefault();
    try {
      const payload = {
        ...createFormData,
        assigned_cleaner_id: createFormData.assigned_cleaner_id || null,
        custom_interval_days: createFormData.frequency === 'CUSTOM' ? parseInt(createFormData.custom_interval_days) : null,
        estimated_duration_minutes: parseInt(createFormData.estimated_duration_minutes)
      };
      await axios.post(`${API_URL}/api/recurring-jobs`, payload);
      toast.success('Recurring job created successfully');
      setDialogOpen(false);
      setCreateFormData(emptyFormData);
      fetchRecurringJobs(filter);
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to create recurring job');
    }
  };

  const handleEdit = (job) => {
    setSelectedJob(job);
    setEditFormData({
      assigned_cleaner_id: job.assigned_cleaner_id || '',
      preferred_time: job.preferred_time,
      estimated_duration_minutes: job.estimated_duration_minutes,
      notes: job.notes || '',
      frequency: job.frequency,
      custom_interval_days: job.custom_interval_days || 7
    });
    setEditDialogOpen(true);
  };

  const handleUpdate = async (e) => {
    e.preventDefault();
    try {
      const payload = {
        assigned_cleaner_id: editFormData.assigned_cleaner_id || null,
        preferred_time: editFormData.preferred_time,
        estimated_duration_minutes: parseInt(editFormData.estimated_duration_minutes),
        notes: editFormData.notes,
        frequency: editFormData.frequency,
        custom_interval_days: editFormData.frequency === 'CUSTOM' ? parseInt(editFormData.custom_interval_days) : null
      };
      await axios.patch(`${API_URL}/api/recurring-jobs/${selectedJob.id}`, payload);
      toast.success('Recurring job updated successfully');
      setEditDialogOpen(false);
      setSelectedJob(null);
      setEditFormData(emptyFormData);
      fetchRecurringJobs(filter);
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to update recurring job');
    }
  };

  const openArchiveDialog = (job) => {
    setJobToArchive(job);
    setArchiveDialogOpen(true);
  };

  const handleArchive = async (jobId) => {
    try {
      await axios.patch(`${API_URL}/api/recurring-jobs/${jobId}/archive`);
      toast.success('Recurring job archived');
      fetchRecurringJobs(filter);
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to archive recurring job');
    } finally {
      setArchiveDialogOpen(false);
      setJobToArchive(null);
    }
  };

  const handleRestore = async (jobId) => {
    try {
      await axios.patch(`${API_URL}/api/recurring-jobs/${jobId}/restore`);
      toast.success('Recurring job restored');
      fetchRecurringJobs(filter);
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to restore recurring job');
    }
  };

  const openGenerateDialog = (job) => {
    setJobToGenerate(job);
    setWeeksToGenerate(4);
    setGenerateDialogOpen(true);
  };

  const handleGenerate = async () => {
    if (!jobToGenerate) return;
    setGenerating(true);
    try {
      const response = await axios.post(`${API_URL}/api/recurring-jobs/${jobToGenerate.id}/generate`, {
        weeks: weeksToGenerate
      });
      toast.success(response.data.message);
      fetchRecurringJobs(filter);
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to generate visits');
    } finally {
      setGenerating(false);
      setGenerateDialogOpen(false);
      setJobToGenerate(null);
    }
  };

  const handleGenerateAll = async () => {
    setGenerating(true);
    try {
      const response = await axios.post(`${API_URL}/api/recurring-jobs/generate-all`, {
        weeks: 4
      });
      const totalCreated = response.data.reduce((sum, r) => sum + r.visits_created, 0);
      toast.success(`Generated ${totalCreated} visit(s) across ${response.data.length} recurring job(s)`);
      fetchRecurringJobs(filter);
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to generate visits');
    } finally {
      setGenerating(false);
    }
  };

  // Stable handlers using useCallback
  const handleCreateClientChange = useCallback((value) => {
    setCreateFormData(prev => ({ ...prev, client_id: value, location_id: '' }));
  }, []);

  const handleCreateLocationChange = useCallback((value) => {
    setCreateFormData(prev => ({ ...prev, location_id: value }));
  }, []);

  const handleCreateCleanerChange = useCallback((value) => {
    setCreateFormData(prev => ({ ...prev, assigned_cleaner_id: value === "unassigned" ? '' : value }));
  }, []);

  const handleCreateStartDateChange = useCallback((e) => {
    const value = e.target.value;
    setCreateFormData(prev => ({ ...prev, start_date: value }));
  }, []);

  const handleCreateTimeChange = useCallback((e) => {
    const value = e.target.value;
    setCreateFormData(prev => ({ ...prev, preferred_time: value }));
  }, []);

  const handleCreateDurationChange = useCallback((e) => {
    const value = e.target.value;
    setCreateFormData(prev => ({ ...prev, estimated_duration_minutes: value }));
  }, []);

  const handleCreateNotesChange = useCallback((e) => {
    const value = e.target.value;
    setCreateFormData(prev => ({ ...prev, notes: value }));
  }, []);

  const handleCreateFrequencyChange = useCallback((value) => {
    setCreateFormData(prev => ({ ...prev, frequency: value }));
  }, []);

  const handleCreateIntervalChange = useCallback((e) => {
    const value = e.target.value;
    setCreateFormData(prev => ({ ...prev, custom_interval_days: value }));
  }, []);

  const handleEditCleanerChange = useCallback((value) => {
    setEditFormData(prev => ({ ...prev, assigned_cleaner_id: value === "unassigned" ? '' : value }));
  }, []);

  const handleEditTimeChange = useCallback((e) => {
    const value = e.target.value;
    setEditFormData(prev => ({ ...prev, preferred_time: value }));
  }, []);

  const handleEditDurationChange = useCallback((e) => {
    const value = e.target.value;
    setEditFormData(prev => ({ ...prev, estimated_duration_minutes: value }));
  }, []);

  const handleEditNotesChange = useCallback((e) => {
    const value = e.target.value;
    setEditFormData(prev => ({ ...prev, notes: value }));
  }, []);

  const handleEditFrequencyChange = useCallback((value) => {
    setEditFormData(prev => ({ ...prev, frequency: value }));
  }, []);

  const handleEditIntervalChange = useCallback((e) => {
    const value = e.target.value;
    setEditFormData(prev => ({ ...prev, custom_interval_days: value }));
  }, []);

  const handleCreateDialogChange = useCallback((open) => {
    setDialogOpen(open);
    if (!open) {
      setCreateFormData(emptyFormData);
    }
  }, []);

  const handleEditDialogChange = useCallback((open) => {
    setEditDialogOpen(open);
    if (!open) {
      setSelectedJob(null);
      setEditFormData(emptyFormData);
    }
  }, []);

  const formatDate = (dateStr) => {
    if (!dateStr) return '-';
    return new Date(dateStr).toLocaleDateString('en-GB');
  };

  return (
    <Layout>
      <div className="space-y-6" data-testid="recurring-jobs-page">
        <div className="flex justify-end items-center gap-3">
          <Select value={filter} onValueChange={setFilter}>
            <SelectTrigger className="w-40" data-testid="recurring-job-filter">
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="active">Active</SelectItem>
              <SelectItem value="archived">Archived</SelectItem>
              <SelectItem value="all">All</SelectItem>
            </SelectContent>
          </Select>
          <Button
            variant="outline"
            onClick={handleGenerateAll}
            disabled={generating || filter !== 'active'}
            data-testid="generate-all-button"
          >
            <RefreshCw className={`w-4 h-4 mr-2 ${generating ? 'animate-spin' : ''}`} />
            Generate All (4 weeks)
          </Button>
          <Dialog open={dialogOpen} onOpenChange={handleCreateDialogChange}>
            <DialogTrigger asChild>
              <Button className="bg-orange-500 hover:bg-orange-600" data-testid="add-recurring-job-button">
                <Plus className="w-4 h-4 mr-2" />
                Add Recurring Job
              </Button>
            </DialogTrigger>
            <DialogContent className="max-w-lg">
              <DialogHeader>
                <DialogTitle>Create Recurring Job</DialogTitle>
              </DialogHeader>
              <form onSubmit={handleSubmit} className="space-y-4" data-testid="recurring-job-form">
                <div>
                    <Label htmlFor="client_id">Client</Label>
                    <Select value={createFormData.client_id || undefined} onValueChange={handleCreateClientChange}>
                      <SelectTrigger data-testid="recurring-job-client-select">
                        <SelectValue placeholder="Select client" />
                      </SelectTrigger>
                      <SelectContent>
                        {clients.filter(c => c.id).map((client) => (
                          <SelectItem key={client.id} value={client.id}>
                            {client.name}
                          </SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </div>
                  <div>
                    <Label htmlFor="location_id">Location</Label>
                    <Select 
                      value={createFormData.location_id || undefined} 
                      onValueChange={handleCreateLocationChange}
                      disabled={!createFormData.client_id}
                    >
                      <SelectTrigger data-testid="recurring-job-location-select">
                        <SelectValue placeholder={createFormData.client_id ? "Select location" : "Select client first"} />
                      </SelectTrigger>
                      <SelectContent>
                        {filteredLocations.map((location) => (
                          <SelectItem key={location.id} value={location.id}>
                            {location.address_line1}, {location.postcode}
                          </SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </div>
                  <div>
                    <Label htmlFor="assigned_cleaner_id">Assigned Cleaner (optional)</Label>
                    <Select value={createFormData.assigned_cleaner_id || "unassigned"} onValueChange={handleCreateCleanerChange}>
                      <SelectTrigger data-testid="recurring-job-cleaner-select">
                        <SelectValue placeholder="Unassigned" />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="unassigned">Unassigned</SelectItem>
                        {cleaners.map((cleaner) => (
                          <SelectItem key={cleaner.id} value={cleaner.id}>
                            {cleaner.full_name}
                          </SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </div>
                  <div className="grid grid-cols-2 gap-4">
                    <div>
                      <Label htmlFor="start_date">Start Date</Label>
                      <Input
                        id="start_date"
                        type="date"
                        value={createFormData.start_date}
                        onChange={handleCreateStartDateChange}
                        required
                        data-testid="recurring-job-start-date"
                      />
                    </div>
                    <div>
                      <Label htmlFor="preferred_time">Preferred Time</Label>
                      <Input
                        id="preferred_time"
                        type="time"
                        value={createFormData.preferred_time}
                        onChange={handleCreateTimeChange}
                        required
                        data-testid="recurring-job-time"
                      />
                    </div>
                  </div>
                  <div>
                    <Label htmlFor="estimated_duration_minutes">Duration (minutes)</Label>
                    <Input
                      id="estimated_duration_minutes"
                      type="number"
                      min="15"
                      max="480"
                      value={createFormData.estimated_duration_minutes}
                      onChange={handleCreateDurationChange}
                      required
                      data-testid="recurring-job-duration"
                    />
                  </div>
                  <div>
                    <Label htmlFor="frequency">Frequency</Label>
                    <Select value={createFormData.frequency} onValueChange={handleCreateFrequencyChange}>
                      <SelectTrigger data-testid="recurring-job-frequency-select">
                        <SelectValue />
                      </SelectTrigger>
                      <SelectContent>
                        {Object.entries(frequencyLabels).map(([value, label]) => (
                          <SelectItem key={value} value={value}>
                            {label}
                          </SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </div>
                  {createFormData.frequency === 'CUSTOM' && (
                    <div>
                      <Label htmlFor="custom_interval_days">Custom Interval (days)</Label>
                      <Input
                        id="custom_interval_days"
                        type="number"
                        min="1"
                        max="365"
                        value={createFormData.custom_interval_days}
                        onChange={handleCreateIntervalChange}
                        required
                        data-testid="recurring-job-custom-interval"
                      />
                    </div>
                  )}
                  <div>
                    <Label htmlFor="notes">Notes</Label>
                    <Textarea
                      id="notes"
                      value={createFormData.notes}
                      onChange={handleCreateNotesChange}
                      rows={2}
                      data-testid="recurring-job-notes"
                    />
                  </div>
                  <Button type="submit" className="w-full bg-orange-500 hover:bg-orange-600" data-testid="submit-recurring-job-button">
                    Create Recurring Job
                  </Button>
                </form>
              </DialogContent>
            </Dialog>
        </div>

        {loading ? (
          <div>Loading...</div>
        ) : recurringJobs.length === 0 ? (
          <div className="bg-white border border-slate-200 p-8 text-center">
            <Calendar className="w-12 h-12 mx-auto text-slate-400 mb-4" />
            <p className="text-slate-600">No recurring jobs found.</p>
            <p className="text-sm text-slate-500 mt-1">Create a recurring job to automatically generate visits.</p>
          </div>
        ) : (
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            {recurringJobs.map((job) => (
              <div
                key={job.id}
                className={`bg-white border p-6 hover:border-slate-300 transition-colors ${
                  job.is_active ? 'border-slate-200' : 'border-slate-300 bg-slate-50'
                }`}
                data-testid={`recurring-job-card-${job.id}`}
              >
                <div className="flex items-start justify-between mb-4">
                  <div className="flex-1">
                    <div className="flex items-center gap-2">
                      <h3 className="font-semibold text-slate-900">{job.client_name}</h3>
                      {!job.is_active && (
                        <span className="px-2 py-0.5 text-xs font-bold bg-slate-200 text-slate-700 border border-slate-300">
                          ARCHIVED
                        </span>
                      )}
                    </div>
                    <p className="text-sm text-slate-600 mt-1">
                      {job.location_address}, {job.location_postcode}
                    </p>
                  </div>
                  <span className="px-2 py-1 text-xs font-medium bg-orange-100 text-orange-700 border border-orange-200">
                    {frequencyLabels[job.frequency]?.split(' ')[0] || job.frequency}
                    {job.frequency === 'CUSTOM' && ` (${job.custom_interval_days}d)`}
                  </span>
                </div>
                
                <div className="space-y-2 text-sm text-slate-600">
                  <div className="flex justify-between">
                    <span>Time:</span>
                    <span className="font-medium">{job.preferred_time}</span>
                  </div>
                  <div className="flex justify-between">
                    <span>Duration:</span>
                    <span className="font-medium">{job.estimated_duration_minutes} min</span>
                  </div>
                  <div className="flex justify-between">
                    <span>Cleaner:</span>
                    <span className="font-medium">{job.assigned_cleaner_name || 'Unassigned'}</span>
                  </div>
                  <div className="flex justify-between">
                    <span>Start Date:</span>
                    <span className="font-medium">{formatDate(job.start_date)}</span>
                  </div>
                  {job.last_generated_until && (
                    <div className="flex justify-between">
                      <span>Generated Until:</span>
                      <span className="font-medium text-emerald-600">{formatDate(job.last_generated_until)}</span>
                    </div>
                  )}
                  {job.notes && (
                    <p className="text-xs text-slate-500 mt-2 pt-2 border-t border-slate-100">{job.notes}</p>
                  )}
                </div>

                <div className="flex gap-2 pt-4 mt-4 border-t border-slate-200">
                  {job.is_active && (
                    <Button
                      variant="outline"
                      size="sm"
                      onClick={() => openGenerateDialog(job)}
                      className="flex-1 border-emerald-200 text-emerald-600 hover:bg-emerald-50"
                      data-testid={`generate-job-${job.id}`}
                    >
                      <Play className="w-3 h-3 mr-1" />
                      Generate
                    </Button>
                  )}
                  <Button
                    variant="outline"
                    size="sm"
                    onClick={() => handleEdit(job)}
                    className="flex-1"
                    data-testid={`edit-job-${job.id}`}
                  >
                    <Edit className="w-3 h-3 mr-1" />
                    Edit
                  </Button>
                  {job.is_active ? (
                    <Button
                      variant="outline"
                      size="sm"
                      onClick={() => openArchiveDialog(job)}
                      className="border-slate-200 text-slate-600 hover:bg-slate-50"
                      data-testid={`archive-job-${job.id}`}
                    >
                      <Archive className="w-3 h-3 mr-1" />
                      Archive
                    </Button>
                  ) : (
                    <Button
                      variant="outline"
                      size="sm"
                      onClick={() => handleRestore(job.id)}
                      className="border-emerald-200 text-emerald-600 hover:bg-emerald-50"
                      data-testid={`restore-job-${job.id}`}
                    >
                      Restore
                    </Button>
                  )}
                </div>
              </div>
            ))}
          </div>
        )}

        {/* Edit Dialog */}
        <Dialog open={editDialogOpen} onOpenChange={handleEditDialogChange}>
          <DialogContent className="max-w-lg">
            <DialogHeader>
              <DialogTitle>Edit Recurring Job</DialogTitle>
            </DialogHeader>
            <form onSubmit={handleUpdate} className="space-y-4" data-testid="edit-recurring-job-form">
              <div>
                <Label htmlFor="edit_assigned_cleaner_id">Assigned Cleaner</Label>
                <Select value={editFormData.assigned_cleaner_id || "unassigned"} onValueChange={handleEditCleanerChange}>
                  <SelectTrigger data-testid="edit-recurring-job-cleaner-select">
                    <SelectValue placeholder="Unassigned" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="unassigned">Unassigned</SelectItem>
                    {cleaners.map((cleaner) => (
                      <SelectItem key={cleaner.id} value={cleaner.id}>
                        {cleaner.full_name}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
              <div>
                <Label htmlFor="edit_preferred_time">Preferred Time</Label>
                <Input
                  id="edit_preferred_time"
                  type="time"
                  value={editFormData.preferred_time}
                  onChange={handleEditTimeChange}
                  required
                  data-testid="edit-recurring-job-time"
                />
              </div>
              <div>
                <Label htmlFor="edit_estimated_duration_minutes">Duration (minutes)</Label>
                <Input
                  id="edit_estimated_duration_minutes"
                  type="number"
                  min="15"
                  max="480"
                  value={editFormData.estimated_duration_minutes}
                  onChange={handleEditDurationChange}
                  required
                  data-testid="edit-recurring-job-duration"
                />
              </div>
              <div>
                <Label htmlFor="edit_frequency">Frequency</Label>
                <Select value={editFormData.frequency} onValueChange={handleEditFrequencyChange}>
                  <SelectTrigger data-testid="edit-recurring-job-frequency-select">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    {Object.entries(frequencyLabels).map(([value, label]) => (
                      <SelectItem key={value} value={value}>
                        {label}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
              {editFormData.frequency === 'CUSTOM' && (
                <div>
                  <Label htmlFor="edit_custom_interval_days">Custom Interval (days)</Label>
                  <Input
                    id="edit_custom_interval_days"
                    type="number"
                    min="1"
                    max="365"
                    value={editFormData.custom_interval_days}
                    onChange={handleEditIntervalChange}
                    required
                    data-testid="edit-recurring-job-custom-interval"
                  />
                </div>
              )}
              <div>
                <Label htmlFor="edit_notes">Notes</Label>
                <Textarea
                  id="edit_notes"
                  value={editFormData.notes}
                  onChange={handleEditNotesChange}
                  rows={2}
                  data-testid="edit-recurring-job-notes"
                />
              </div>
              <Button type="submit" className="w-full bg-orange-500 hover:bg-orange-600" data-testid="update-recurring-job-button">
                Update Recurring Job
              </Button>
            </form>
          </DialogContent>
        </Dialog>

        {/* Archive Confirmation Dialog */}
        <AlertDialog open={archiveDialogOpen} onOpenChange={setArchiveDialogOpen}>
          <AlertDialogContent>
            <AlertDialogHeader>
              <AlertDialogTitle>Archive Recurring Job</AlertDialogTitle>
              <AlertDialogDescription>
                Are you sure you want to archive this recurring job for "{jobToArchive?.client_name}"? No new visits will be generated for archived jobs.
              </AlertDialogDescription>
            </AlertDialogHeader>
            <AlertDialogFooter>
              <AlertDialogCancel>Cancel</AlertDialogCancel>
              <AlertDialogAction 
                onClick={() => handleArchive(jobToArchive?.id)}
                className="bg-orange-500 hover:bg-orange-600"
              >
                Archive
              </AlertDialogAction>
            </AlertDialogFooter>
          </AlertDialogContent>
        </AlertDialog>

        {/* Generate Confirmation Dialog */}
        <AlertDialog open={generateDialogOpen} onOpenChange={setGenerateDialogOpen}>
          <AlertDialogContent>
            <AlertDialogHeader>
              <AlertDialogTitle>Generate Visits</AlertDialogTitle>
              <AlertDialogDescription>
                Generate visits for "{jobToGenerate?.client_name}" at {jobToGenerate?.location_address}?
              </AlertDialogDescription>
            </AlertDialogHeader>
            <div className="py-4">
              <Label htmlFor="weeks_to_generate">Weeks to generate</Label>
              <Input
                id="weeks_to_generate"
                type="number"
                min="1"
                max="52"
                value={weeksToGenerate}
                onChange={(e) => setWeeksToGenerate(parseInt(e.target.value) || 4)}
                className="mt-2"
                data-testid="weeks-to-generate-input"
              />
            </div>
            <AlertDialogFooter>
              <AlertDialogCancel disabled={generating}>Cancel</AlertDialogCancel>
              <AlertDialogAction 
                onClick={handleGenerate}
                disabled={generating}
                className="bg-emerald-500 hover:bg-emerald-600"
              >
                {generating ? 'Generating...' : `Generate ${weeksToGenerate} Week(s)`}
              </AlertDialogAction>
            </AlertDialogFooter>
          </AlertDialogContent>
        </AlertDialog>
      </div>
    </Layout>
  );
}
